import { useState, useEffect, useMemo } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API } from '@/App';
import axios from 'axios';

// Common timezones for quick access
const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
  'Pacific/Auckland',
];

export default function TimezoneSelect({ value, onChange, placeholder = "Select timezone...", disabled = false }) {
  const [open, setOpen] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [groupedTimezones, setGroupedTimezones] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTimezones();
  }, []);

  const fetchTimezones = async () => {
    try {
      const response = await axios.get(`${API}/timezones`);
      setTimezones(response.data.timezones || []);
      setGroupedTimezones(response.data.grouped || {});
    } catch (error) {
      console.error('Failed to fetch timezones:', error);
      // Fallback to common timezones if API fails
      setTimezones(COMMON_TIMEZONES);
    } finally {
      setLoading(false);
    }
  };

  // Filter timezones based on search
  const filteredTimezones = useMemo(() => {
    if (!searchQuery) {
      // Show common timezones first when no search
      return COMMON_TIMEZONES;
    }
    const query = searchQuery.toLowerCase();
    return timezones.filter(tz => 
      tz.toLowerCase().includes(query)
    ).slice(0, 50); // Limit to 50 results for performance
  }, [searchQuery, timezones]);

  // Get display label for timezone
  const getTimezoneLabel = (tz) => {
    if (!tz) return '';
    // Format: "America/New_York" -> "New York (America)"
    const parts = tz.split('/');
    if (parts.length >= 2) {
      const city = parts[parts.length - 1].replace(/_/g, ' ');
      const region = parts[0];
      return `${city} (${region})`;
    }
    return tz;
  };

  const selectedLabel = value ? getTimezoneLabel(value) : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className="w-full justify-between font-normal"
        >
          <div className="flex items-center gap-2 truncate">
            <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{value || placeholder}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search timezone..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>No timezone found.</CommandEmpty>
            {!searchQuery && (
              <CommandGroup heading="Common Timezones">
                {COMMON_TIMEZONES.map((tz) => (
                  <CommandItem
                    key={tz}
                    value={tz}
                    onSelect={() => {
                      onChange(tz);
                      setOpen(false);
                      setSearchQuery('');
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === tz ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="font-medium">{getTimezoneLabel(tz)}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{tz}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {searchQuery && (
              <CommandGroup heading="Search Results">
                {filteredTimezones.map((tz) => (
                  <CommandItem
                    key={tz}
                    value={tz}
                    onSelect={() => {
                      onChange(tz);
                      setOpen(false);
                      setSearchQuery('');
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === tz ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="font-medium">{getTimezoneLabel(tz)}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{tz}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
